/**
  Season ticket holder waiting list component
 */
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HolderModel } from '../models/holder.model';
import LoaderComponent from './loader.component';
import { Alert, AlertTypes } from './alert.component'
import InputContainer from './input.component'
import SthCheckBox from './sth-checkbox.component';
import styles from './waiting-list-form.module.scss';
import { canadaProvinceList, learnMore, usaArmedForces, usaStateList } from '../util/form-values';
import { useOktaAuth } from '@okta/okta-react';
import { CmsConfigs } from '../models/configs.model';
import { getConfigs, getHomepath } from '../util/cms-utils';
import { HolderService, HOLDER_STATUS_NOT_ENTITLED, HOLDER_STATUS_VERIFY_EMAIL } from '../services/holder.service';
import { Constants } from '../util/constants';
import SafeAnchor from './safe-anchor.component';
import { validateHolder } from '../util/validation-helpers';
import { RequiredAsterisk, SelectOne } from './utils.component';
import { getLogoutUrl } from '../util/cms-utils';

// Setting up values for date picker
const months: Array<string> = [];
for (let i = 1; i < 13; i += 1) {
  months.push(i.toString().padStart(2, '0'));
}

const days: Array<string> = [];
for (let i = 1; i < 32; i += 1) {
  days.push(i.toString().padStart(2, '0'));
}

const years: Array<string> = [];
const currYear = new Date().getFullYear();
for (let i = currYear; i >= currYear - 100; i -= 1) {
  years.push(i.toString());
}

interface OwnProps {
  holderHost: string;
}

interface ErrorState {
  alertType: AlertTypes;
  text: string;
}

const WaitingListForm = ({ holderHost }: OwnProps) => {
  const { authState, oktaAuth } = useOktaAuth()
  const holderService: HolderService = new HolderService(authState, holderHost);
  const initialHolder: HolderModel = {
    firstName: '',
    lastName: '',
    company: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
    phoneNumberPrimary: '',
    phoneNumberSecondary: '',
    dateOfBirth: ['', '', ''],
    learnMore: [],
    clubCode: 'chc',
    venueId: '17',
    cubsSms: false,
    cubsAlertsSms: false,
  };

  const [alert, setAlert] = useState<ErrorState | null>();
  const [holder, setHolder] = useState<HolderModel>(initialHolder);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [showFormErrors, setShowFormErrors] = useState<boolean>(false);
  const [config, setConfig] = useState<CmsConfigs>();
  const query = new URLSearchParams(useLocation().search);
  const [isCubsAlertsSmsDisabled, setCubsAlertsSmsDisabled] = useState<boolean>(false);

  useEffect(() => {
    const config: CmsConfigs = getConfigs(query.get("clubCode"));
    init(config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [/* on mount */]);

  const init: (config: CmsConfigs) => void = async (config: CmsConfigs): Promise<void> => {
    let result: HolderModel | undefined;
    try {
      setShowLoading(true);
      setConfig(config);
      result = await holderService.getValidationStatus(config.clubCode, config.venueId);
      if (result && result.accountId) {
        // If the cubsAlertsSms field was already checked and submitted we don't want to give the impression that
        // unchecking the box will result in unsubscribing from the list (as there isn't currently an API for that).
        setCubsAlertsSmsDisabled(result.cubsAlertsSms);
        setHolder(result);
      }
    } catch (_error) {
      const error = _error as Error
      if (error.message === HOLDER_STATUS_VERIFY_EMAIL) {
        // app will show email verify button
        window.parent.location.href = `${getHomepath(config)}/app`;
      } else if (error.message !== HOLDER_STATUS_NOT_ENTITLED) {
        // Entitlement will be granted after signing up, so don't show an alert
        setAlert({
          alertType: 'error',
          text: Constants.GENERAL_ERROR,
        });
      }
    } finally {
      if (!result || !result.email) {
        try {
          const { email } = await oktaAuth.getUser();
          setHolder(prevHolder => ({ ...prevHolder, email }));
        } catch (err) {
          console.error(err, 'could not find email')
        }
      }
      setShowLoading(false);
    }
  };

  const handleInputChange: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void =
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
      event.persist();
      const target = event.target;
      const name = target.name
      const value = target.type === 'checkbox' ? (target as HTMLInputElement).checked : target.value

      // also reset the state
      const isCountryChanged = name === 'country';

      setHolder(prevHolder => ({
        ...prevHolder,
        [name]: value,
        ...(isCountryChanged && ({ state: '' })),
      }));
    };

  const handleBirthDateChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const { dateOfBirth } = holder;
    const { target: { name, value } } = event;

    const mapping: Record<string, number> = {
      year: 0,
      month: 1,
      day: 2,
    }

    let newDateOfBirth = [...dateOfBirth]
    newDateOfBirth[mapping[name]] = value

    setHolder(prevHolder => ({
      ...prevHolder,
      dateOfBirth: newDateOfBirth,
    }));
  };

  const checkboxCallback: (traitName: string, traitVals: Array<string>) => void =
    (traitName: string, traitVals: Array<string>): void => {
      setHolder(prevHolder => ({
        ...prevHolder,
        [traitName]: traitVals
      }));
    };

  const handleFormSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void> =
    async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
      setAlert(null);
      event.preventDefault();
      const hasFormErrors = Object.values(validateHolder(holder)).some(Boolean);
      if (hasFormErrors) {
        setShowFormErrors(true);
        setAlert({ alertType: 'error', text: Constants.FORM_ERROR });
        return
      }

      try {
        setShowLoading(true);
        const isSuccess = await holderService.publishHolder(holder, Boolean(holder.accountId));
        setAlert({
          alertType: 'success',
          text: Constants.SUCCESS_PUBLISH_HOLDER,
        });
        if (isSuccess) {
          window.parent.location.href = `${getHomepath(config)}/app`;
        } else {
          window.parent.location.href = `${getHomepath(config)}`;
        }
      } catch (error) {
        console.error('Error submitting form', error)
        setAlert({
          alertType: 'error',
          text: Constants.FAILED_PUBLISH_HOLDER,
        });
      } finally {
        setShowLoading(false);
      }
    };

  const isUnitedStates = holder.country === 'USA';
  const validationErrors: any = showFormErrors ? validateHolder(holder) : {};

  return (
    <div className={styles.container}>
      <p className={styles.formTitle}>Season Ticket Holder Waiting List Form</p>
      <form className={styles.form} onSubmit={handleFormSubmit}>
        {/* profile */}
        <div className={styles.profileContainer}>
          <div className={styles.flexRow}>
            <InputContainer
              className={styles.rowContainer}
              hasError={validationErrors.firstName}
              errorMessage='Please enter your first name.'
              label='First Name'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                id: 'firstName',
                name: 'firstName',
                value: holder.firstName,
                onChange: handleInputChange,
                required: true,
                maxLength: 80,
              }}
            />
            <InputContainer
              className={styles.rowContainer}
              hasError={validationErrors.lastName}
              errorMessage='Please enter your last name.'
              label='Last Name'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                id: 'lastName',
                name: 'lastName',
                value: holder.lastName,
                onChange: handleInputChange,
                required: true,
                maxLength: 80,
              }}
            />
          </div>
          {holder.email && (
            <div className={styles.flexRow}>
              <InputContainer
                className={styles.rowContainer}
                label='Email Address'
                labelProps={{ className: styles.label }}
                inputProps={{
                  className: styles.textBox,
                  value: holder.email,
                  disabled: true,
                  required: true,
                }}
              />
              <p className={styles.emailMessage}>
                Not seeing the right account info? <SafeAnchor anchorProps={{ href: `${getLogoutUrl(config)}`, target: '_parent' }}>Log out</SafeAnchor> and back in using another email address, or reach out to <a href='mailto:cubswaitinglist@cubs.com'>cubswaitinglist@cubs.com</a> for further assistance.
              </p>
            </div>
          )}
          <div className={`${styles.flexRow} ${styles.singleColumn}`}>
            <InputContainer
              className={styles.rowContainer}
              label='Company Name'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                name: 'company',
                id: 'company',
                value: holder.company,
                onChange: handleInputChange,
                maxLength: 100,
              }}
            />
          </div>
          <div className={styles.flexRow}>
            <InputContainer
              className={styles.rowContainer}
              hasError={validationErrors.address1}
              errorMessage='Please enter your address.'
              label='Address'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                name: 'address1',
                id: 'address1',
                value: holder.address1,
                onChange: handleInputChange,
                required: true,
                maxLength: 100,
              }}
            />
            <InputContainer
              className={styles.rowContainer}
              label='Address 2'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                name: 'address2',
                id: 'address2',
                value: holder.address2,
                onChange: handleInputChange,
                maxLength: 100,
              }}
            />
          </div>
          <div className={styles.flexRow}>
            <InputContainer
              className={styles.rowContainer}
              hasError={validationErrors.city}
              errorMessage='Please enter your city.'
              label='City'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                name: 'city',
                id: 'city',
                value: holder.city,
                onChange: handleInputChange,
                required: true,
                maxLength: 50,
              }}
            />
            <div className={styles.selectRow}>
              <label className={styles.label} htmlFor='state'><RequiredAsterisk /> {isUnitedStates ? 'State' : 'Province'}</label>
              <select className={styles.selectItem} name='state' id='state' value={holder.state} onChange={handleInputChange} required>
                <SelectOne />
                {isUnitedStates ? (
                  <>
                    <optgroup label='States'>
                      {usaStateList.map(({ abbr, name }) => (
                        <option key={abbr} value={abbr}>{abbr} - {name}</option>
                      ))}
                    </optgroup>
                    <optgroup label='US Armed Forces'>
                      {usaArmedForces.map(({ abbr, name }) => (
                        <option key={`${abbr}-${name}`} value={abbr}>{abbr} - {name}</option>
                      ))}
                    </optgroup>
                  </>
                ) : (
                    <optgroup label='Provinces'>
                      {canadaProvinceList.map(({ abbr, name }) => (
                        <option key={abbr} value={abbr}>{abbr} - {name}</option>
                      ))}
                    </optgroup>
                  )}
              </select>
            </div>
          </div>
          <div className={styles.flexRow}>
            <InputContainer
              className={styles.rowContainer}
              hasError={validationErrors.zipCode}
              errorMessage='Please enter a valid zip/postal code.'
              label='Zip/Postal Code'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                name: 'zipCode',
                id: 'zipCode',
                value: holder.zipCode,
                onChange: handleInputChange,
                required: true,
                maxLength: isUnitedStates ? 5 : 7,
              }}
            />
            <div className={styles.selectRow}>
              <label className={styles.label} htmlFor='country'><RequiredAsterisk /> Country</label>
              <select className={styles.selectItem} name='country' id='country' value={holder.country} onChange={handleInputChange}>
                <option value='USA'>United States</option>
                <option value='CAN'>Canada</option>
              </select>
            </div>
          </div>
          <div className={styles.flexRow}>
            <InputContainer
              className={styles.rowContainer}
              hasError={validationErrors.phoneNumberPrimary}
              errorMessage='Please enter a valid mobile phone number.'
              label='Mobile Phone'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                name: 'phoneNumberPrimary',
                id: 'phoneNumberPrimary',
                value: holder.phoneNumberPrimary,
                onChange: handleInputChange,
                type: 'tel',
                required: true,
                maxLength: 10,
                placeholder: '1234567890',
              }}
            />
            <InputContainer
              className={styles.rowContainer}
              hasError={validationErrors.phoneNumberSecondary}
              errorMessage='Please enter a valid secondary phone number.'
              label='Secondary Phone'
              labelProps={{ className: styles.label }}
              inputProps={{
                className: styles.textBox,
                name: 'phoneNumberSecondary',
                id: 'phoneNumberSecondary',
                value: holder.phoneNumberSecondary,
                onChange: handleInputChange,
                type: 'tel',
                maxLength: 10,
                placeholder: '1234567890',
              }}
            />
          </div>
          <div className={styles.flexRow}>
            <div className={styles.selectRow}>
              <label className={styles.label}><RequiredAsterisk /> Birth Date</label>
              <select className={styles.selectDate} name='month' onChange={handleBirthDateChange} value={holder.dateOfBirth[1]} required>
                <option value=''>- mm -</option>
                {months.map(month => (<option key={month} value={month}>{month}</option>))}
              </select>
              <select className={styles.selectDate} name='day' onChange={handleBirthDateChange} value={holder.dateOfBirth[2]} required>
                <option value=''>- dd -</option>
                {days.map(day => (<option key={day} value={day}>{day}</option>))}
              </select>
              <select name='year' onChange={handleBirthDateChange} value={holder.dateOfBirth[0]} required>
                <option value=''>- yyyy -</option>
                {years.map(year => (<option key={year} value={year}>{year}</option>))}
              </select>
            </div>
            <div style={{ flex: 1 }} />
          </div>
        </div>
        <hr />
        {/* survey traits */}
        <div className={styles.surveyContainer}>
          <label><RequiredAsterisk /> Would you be interested in learning more about any of the following Cubs ticket offers while you wait for your Season Ticket Holder Waiting List number to be called? Check all that apply.</label>
          {validationErrors.learnMore && (<span className={styles.error}>Please select at least one option.</span>)}
          <div className={styles.surveyItems}>
            {learnMore.map(({ label, value }) => (<SthCheckBox key={value} label={label} value={value} name='learnMore' traitVals={holder.learnMore} changeHandler={checkboxCallback} />))}
          </div>
        </div>
        <hr />

        {/* sms opt in */}
        <div className={styles.smsContainer}>
          <div>
            <input
              name='cubsSms'
              id='cubsSms'
              onChange={handleInputChange}
              type='checkbox'
              checked={holder.cubsSms}
            />
            <label htmlFor='cubsSms'>Cubs Mobile Opt-in</label>
          </div>
          <p className={styles.smsDescription}>
            Enter your mobile phone information to receive text messages & updates from the Chicago Cubs (Optional, if checked, ‘Mobile Phone’ must be provided). Msg&Data Rates may apply. Reply STOP to cancel and text HELP for assistance. Expect 1-2 message per week. Not a condition of receiving any goods or services.
          </p>
        </div>
        <hr />

        <div className={styles.smsContainer}>
          <div>
            <input
              name='cubsAlertsSms'
              id='cubsAlertsSms'
              onChange={handleInputChange}
              type='checkbox'
              checked={holder.cubsAlertsSms}
              disabled={isCubsAlertsSmsDisabled}
            />
            <label htmlFor='cubsAlertsSms'>cubsalerts</label>
          </div>
          <p className={styles.smsDescription}>
            Enter your mobile phone information to receive text messages & updates from <SafeAnchor anchorProps={{ href: 'https://cubs.com' }}>Official Chicago Cubs Website | MLB.com</SafeAnchor> (Optional, if checked, 'Mobile Phone' must be provided). Msg&Data rates may apply. Text STOP to cancel to <em>88019</em>. Text HELP or email <SafeAnchor anchorProps={{ href: 'mailto:mlb-mobile-cs@mlb.com' }}>mlb-mobile-cs@mlb.com</SafeAnchor> for assistance. Expect 1-2 messages per week. Not a condition of receiving any goods or services.
          </p>
        </div>
        <hr />

        <div className={styles.buttonContainer}>
          <label className={styles.terms}>By submitting you acknowledge that you have read, understand and agree to be bound by <br /> our <SafeAnchor anchorProps={{ href: 'https://www.mlb.com/official-information/terms-of-use' }}>Website Terms of Use</SafeAnchor> and <SafeAnchor anchorProps={{ href: 'https://www.mlb.com/official-information/privacy-policy' }}>Privacy Policy</SafeAnchor>.</label>

          <button type='submit' className={styles.submitBtn}>Submit</button>
        </div>
      </form>

      { /* Show the alert box and associated text here */}
      {alert ? (
        <div className={styles.alert}>
          <Alert
            alertType={alert ? alert.alertType : 'informational'}
            text={alert ? alert.text : ''}
          />
        </div>
      ) : null}
      { /* Show the loading modal */}
      {showLoading && (
        <div className={styles.loaderContainer}>
          <LoaderComponent />
        </div>
      )}
    </div>
  );
};

export default WaitingListForm;
